import { ReactNode, useState } from "react";
import {
  Box,
  ButtonBase,
  Divider,
  Icon,
  Menu,
  MenuItem,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import { SB, popInAnimation, tabularSx } from "./tokens";

/* ------------------------------------------------------------------ shell */

type ScenarioPopoverProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  /** Dialog title ("Strategies", "Treatments", "Ignition regimes"). */
  title: string;
  /** Item count shown in a pill beside the title. */
  count?: number;
  /** Optional scope note after the count ("in No Mitigation"). */
  scope?: string;
  /** Renders the "New" button when given; hidden for view-only users. */
  onNew?: () => void;
  newLabel?: string;
  footer?: ReactNode;
  children: ReactNode;
};

/**
 * One popover shell for all three scenario-bar segments: header (title, count,
 * scope, New), body, optional footer. Anchored to its segment's left edge,
 * 12px below.
 */
export function ScenarioPopover({
  anchorEl,
  open,
  onClose,
  title,
  count,
  scope,
  onNew,
  newLabel = "New",
  footer,
  children,
}: ScenarioPopoverProps) {
  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          role: "dialog",
          "aria-label": title,
          sx: {
            mt: "12px",
            ml: "-4px",
            width: SB.popoverWidth,
            maxWidth: "calc(100vw - 32px)",
            borderRadius: `${SB.radius}px`,
            background: SB.popover,
            color: SB.text,
            boxShadow: `inset 0 0 0 1px ${SB.ring}, 0 28px 56px -16px rgba(0,0,0,0.65), 0 4px 12px rgba(0,0,0,0.3)`,
            overflow: "hidden",
            ...popInAnimation,
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          pl: 2,
          pr: 1.75,
          pt: 1.75,
          pb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Typography
            component="h2"
            sx={{ m: 0, fontSize: 14, lineHeight: "20px", fontWeight: 600, color: SB.text }}
          >
            {title}
          </Typography>
          {count !== undefined && (
            <Box
              component="span"
              sx={{
                ...tabularSx,
                fontSize: 11,
                lineHeight: "18px",
                px: 0.875,
                borderRadius: 999,
                background: "rgba(255,255,255,0.07)",
                color: SB.textTable,
                fontWeight: 600,
              }}
            >
              {count}
            </Box>
          )}
          {scope && (
            <Typography
              noWrap
              component="span"
              sx={{ fontSize: 12, color: SB.meta, minWidth: 0 }}
            >
              {scope}
            </Typography>
          )}
        </Box>
        {onNew && (
          <ButtonBase
            onClick={onNew}
            sx={{
              flex: "none",
              height: 28,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              pl: 1,
              pr: 1.25,
              borderRadius: "6px",
              background: "rgba(0,212,172,0.12)",
              color: SB.accent,
              fontSize: 12,
              fontWeight: 600,
              transition: "background 120ms",
              "&:hover": { background: "rgba(0,212,172,0.22)" },
              "&:focus-visible": { outline: `2px solid ${SB.accent}`, outlineOffset: 1 },
            }}
          >
            <Icon sx={{ fontSize: 15 }}>add</Icon>
            {newLabel}
          </ButtonBase>
        )}
      </Box>
      <ScenarioDivider />
      {children}
      {footer && (
        <>
          <ScenarioDivider />
          {footer}
        </>
      )}
    </Popover>
  );
}

export const ScenarioDivider = () => (
  <Divider sx={{ borderColor: SB.hairline }} />
);

/* ------------------------------------------------------------------- list */

export const ScenarioList = ({
  children,
  role = "list",
  ariaLabel,
  maxHeight,
}: {
  children: ReactNode;
  role?: "list" | "listbox";
  ariaLabel: string;
  maxHeight?: number;
}) => (
  <Box
    role={role}
    aria-label={ariaLabel}
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: "2px",
      p: 0.75,
      maxHeight,
      overflowY: maxHeight ? "auto" : undefined,
    }}
  >
    {children}
  </Box>
);

/* -------------------------------------------------------------------- row */

export type RowAction = {
  icon: string;
  label: string;
  onClick: () => void;
};

export type RowMenuItem = {
  icon: string;
  label: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  /** Hover text, used to say why an item is disabled. */
  title?: string;
  /** Draws a divider above this item. */
  dividerAbove?: boolean;
};

type ScenarioRowProps = {
  /** Radio-style row: shows a radio and marks the current item. */
  selectable?: boolean;
  selected?: boolean;
  onSelect?: () => void;
  /** Material icon drawn in a tile instead of a radio. */
  icon?: string;
  name: string;
  /** Second line under the name (treatment type · year swatch). */
  subline?: ReactNode;
  /** Right-aligned meta ("4 ignitions", "77.07 ha"). */
  meta?: ReactNode;
  /** Hover/focus-revealed icon buttons (edit). */
  actions?: RowAction[];
  /** Items for the ⋯ menu. The menu is omitted when empty. */
  menuItems?: RowMenuItem[];
};

/**
 * A 44px (48px two-line) row: radio or icon tile, name, meta, then edit + ⋯
 * revealed on hover and focus-within. Delete lives in the ⋯ menu.
 */
export function ScenarioRow({
  selectable = false,
  selected = false,
  onSelect,
  icon,
  name,
  subline,
  meta,
  actions = [],
  menuItems = [],
}: ScenarioRowProps) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(menuAnchor);
  const twoLine = !!subline;
  const hasTrailing = actions.length > 0 || menuItems.length > 0;

  const leading = selectable ? (
    <Box
      component="span"
      aria-hidden="true"
      sx={{
        width: 16,
        height: 16,
        borderRadius: "50%",
        boxShadow: `inset 0 0 0 1.5px ${selected ? SB.accent : SB.radioOff}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "none",
        transition: "box-shadow 120ms",
      }}
    >
      <Box
        component="span"
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: SB.accent,
          transform: selected ? "scale(1)" : "scale(0)",
          transition: "transform 140ms",
        }}
      />
    </Box>
  ) : icon ? (
    <Box
      component="span"
      aria-hidden="true"
      sx={{
        width: 28,
        height: 28,
        borderRadius: "6px",
        background: SB.tile,
        color: SB.textIcon,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "none",
      }}
    >
      <Icon sx={{ fontSize: 16 }}>{icon}</Icon>
    </Box>
  ) : null;

  const body = (
    <>
      {leading}
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <Tooltip title={name} arrow enterDelay={600} enterNextDelay={600}>
          <Typography
            noWrap
            component="span"
            sx={{
              fontSize: 13.5,
              lineHeight: "18px",
              fontWeight: 500,
              color: selected ? "#fff" : SB.textRow,
            }}
          >
            {name}
          </Typography>
        </Tooltip>
        {subline && (
          <Box
            component="span"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              fontSize: 11.5,
              lineHeight: "15px",
              color: SB.meta,
              minWidth: 0,
            }}
          >
            {subline}
          </Box>
        )}
      </Box>
      {meta !== undefined && meta !== null && meta !== "" && (
        <Box
          component="span"
          sx={{
            ...tabularSx,
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            fontSize: 12,
            color: SB.meta,
            whiteSpace: "nowrap",
            flex: "none",
          }}
        >
          {meta}
        </Box>
      )}
    </>
  );

  const bodySx = {
    flex: 1,
    minWidth: 0,
    height: twoLine ? SB.rowHeightTwoLine : SB.rowHeight,
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    pl: 1.5,
    pr: hasTrailing ? 1 : 1.5,
    borderRadius: "8px",
    textAlign: "left" as const,
    font: "inherit",
    color: "inherit",
  };

  return (
    <Box
      role={selectable ? undefined : "listitem"}
      className={menuOpen ? "sb-row sb-row-menu-open" : "sb-row"}
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        borderRadius: "8px",
        background: selected ? "rgba(0,212,172,0.08)" : "transparent",
        transition: "background 120ms",
        "&:hover": {
          background: selected ? "rgba(0,212,172,0.11)" : "rgba(255,255,255,0.04)",
        },
        "& .sb-row-actions": { opacity: 0, transition: "opacity 120ms" },
        "&:hover .sb-row-actions, &:focus-within .sb-row-actions, &.sb-row-menu-open .sb-row-actions":
          { opacity: 1 },
      }}
    >
      {onSelect ? (
        <ButtonBase
          role={selectable ? "option" : undefined}
          aria-selected={selectable ? selected : undefined}
          onClick={onSelect}
          sx={{
            ...bodySx,
            "&:focus-visible": { outline: `2px solid ${SB.accent}`, outlineOffset: -2 },
          }}
        >
          {body}
        </ButtonBase>
      ) : (
        <Box sx={bodySx}>{body}</Box>
      )}

      {hasTrailing && (
        <Box className="sb-row-actions" sx={{ display: "flex", gap: "2px", pr: 0.75 }}>
          {actions.map((action) => (
            <RowIconButton
              key={action.label}
              icon={action.icon}
              label={action.label}
              onClick={action.onClick}
            />
          ))}
          {menuItems.length > 0 && (
            <RowIconButton
              icon="more_horiz"
              label="More actions"
              active={menuOpen}
              ariaHasPopup="menu"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
            />
          )}
        </Box>
      )}

      {menuItems.length > 0 && (
        <RowMenu
          anchorEl={menuAnchor}
          open={menuOpen}
          onClose={() => setMenuAnchor(null)}
          items={menuItems}
        />
      )}
    </Box>
  );
}

const RowIconButton = ({
  icon,
  label,
  onClick,
  active = false,
  ariaHasPopup,
}: {
  icon: string;
  label: string;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  active?: boolean;
  ariaHasPopup?: "menu";
}) => (
  <Tooltip title={label} arrow enterDelay={500}>
    <ButtonBase
      aria-label={label}
      aria-haspopup={ariaHasPopup}
      aria-expanded={ariaHasPopup ? active : undefined}
      onClick={onClick}
      sx={{
        width: 28,
        height: 28,
        borderRadius: "6px",
        color: active ? SB.text : SB.textIcon,
        background: active ? "rgba(255,255,255,0.10)" : "transparent",
        transition: "background 120ms, color 120ms",
        "&:hover": { background: "rgba(255,255,255,0.08)", color: SB.text },
        "&:focus-visible": { outline: `2px solid ${SB.accent}`, outlineOffset: 1 },
      }}
    >
      <Icon sx={{ fontSize: 16 }}>{icon}</Icon>
    </ButtonBase>
  </Tooltip>
);

/* ------------------------------------------------------------------- menu */

export function RowMenu({
  anchorEl,
  open,
  onClose,
  items,
}: {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  items: RowMenuItem[];
}) {
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{
        paper: {
          sx: {
            mt: 0.5,
            width: 200,
            p: 0.5,
            borderRadius: "10px",
            background: SB.menu,
            color: SB.textBody,
            boxShadow:
              "inset 0 0 0 1px rgba(255,255,255,0.09), 0 16px 32px -8px rgba(0,0,0,0.6)",
            ...popInAnimation,
          },
        },
      }}
      MenuListProps={{ sx: { p: 0 } }}
    >
      {items.map((item) => {
        const menuItem = (
          <MenuItem
            key={item.label}
            disabled={item.disabled}
            onClick={() => {
              onClose();
              item.onClick();
            }}
            sx={{
              height: 32,
              minHeight: 32,
              px: 1.25,
              gap: 1.25,
              borderRadius: "6px",
              fontSize: 13,
              color: item.danger ? SB.danger : SB.textBody,
              "&:hover": {
                background: item.danger ? "rgba(252,165,165,0.10)" : "rgba(255,255,255,0.07)",
              },
              "&.Mui-disabled": { opacity: 0.4 },
            }}
          >
            <Icon sx={{ fontSize: 16 }}>{item.icon}</Icon>
            {item.label}
          </MenuItem>
        );
        return [
          item.dividerAbove ? (
            <Divider key={`${item.label}-divider`} sx={{ my: 0.5, mx: 0.75, borderColor: "rgba(255,255,255,0.07)" }} />
          ) : null,
          // A disabled MenuItem swallows pointer events, so wrap it to keep
          // the "why" tooltip working.
          item.disabled && item.title ? (
            <Tooltip key={item.label} title={item.title} arrow placement="left">
              <span>{menuItem}</span>
            </Tooltip>
          ) : (
            menuItem
          ),
        ];
      })}
    </Menu>
  );
}

/* ------------------------------------------------------------ empty state */

export function ScenarioEmptyState({
  icon,
  title,
  body,
  children,
}: {
  icon: string;
  title: string;
  body: string;
  children?: ReactNode;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1.25,
        px: 3,
        pt: 3.5,
        pb: 3.25,
        textAlign: "center",
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          width: 40,
          height: 40,
          borderRadius: "10px",
          background: "rgba(255,255,255,0.05)",
          color: SB.textIcon,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon sx={{ fontSize: 20 }}>{icon}</Icon>
      </Box>
      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: SB.text }}>{title}</Typography>
      <Typography sx={{ fontSize: 12.5, lineHeight: "18px", color: SB.meta, maxWidth: 260 }}>
        {body}
      </Typography>
      {children}
    </Box>
  );
}

/* ----------------------------------------------------------- footer link */

export const FooterLink = ({
  icon,
  label,
  onClick,
  disabled = false,
  quiet = false,
}: {
  icon?: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Grey until hovered, then red: for destructive footer actions. */
  quiet?: boolean;
}) => (
  <ButtonBase
    onClick={onClick}
    disabled={disabled}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      height: 30,
      px: 1.25,
      borderRadius: "6px",
      fontSize: 12.5,
      fontWeight: quiet ? 500 : 600,
      color: quiet ? SB.textIcon : SB.accent,
      transition: "background 120ms, color 120ms",
      "&:hover": quiet
        ? { color: SB.danger, background: "rgba(252,165,165,0.10)" }
        : { background: "rgba(0,212,172,0.10)" },
      "&.Mui-disabled": { color: "rgba(255,255,255,0.3)" },
      "&:focus-visible": { outline: `2px solid ${SB.accent}`, outlineOffset: 1 },
    }}
  >
    {icon && <Icon sx={{ fontSize: 16 }}>{icon}</Icon>}
    {label}
  </ButtonBase>
);

export const StatusLine = ({ children }: { children: ReactNode }) => (
  <Typography sx={{ px: 2, py: 1.5, fontSize: 12.5, color: SB.meta }}>{children}</Typography>
);
